import { FC } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Image, SearchableListbox, SpinningLoader, styles, Text, WeaveLogoIcon } from '@frontend/design-system';
import {
  listStyle,
  listOptionStyle,
  emptyStateContainerStyle,
  responsiveImageStyle,
  itemLabelStyle,
} from './kiosk-forms-or-packets-list.styles';

export interface KioskListItem {
  label: string;
  value: string;
  shouldShowWeaveLogo?: boolean;
  isPinned?: boolean;
  formsCount?: number;
}

interface ListingProps {
  type: 'forms' | 'packets';
  list: KioskListItem[];
  isLoading: boolean;
  onSelect: (value: string | string[]) => void;
}

export const KioskFormOrPacketList: FC<ListingProps> = ({ type, list, isLoading, onSelect }) => {
  const ListItemComponent = type === 'forms' ? KioskFormListItem : KioskPacketListItem;

  if (isLoading) {
    return (
      <div css={styles.flexCenter}>
        <SpinningLoader size='medium' />
      </div>
    );
  }

  if (!list.length) {
    return <EmptyState type={type} />;
  }

  return (
    <SearchableListbox labelId={`kiosk-${type}-list`} value='' css={listStyle} onSelect={onSelect}>
      {list.map((item) => (
        <SearchableListbox.Option
          key={item.value}
          value={item.value}
          searchValue={item.label}
          css={[listOptionStyle, type === 'forms' && css({ '> span': { justifyContent: 'flex-start' } })]}
        >
          <ListItemComponent {...item} />
        </SearchableListbox.Option>
      ))}
    </SearchableListbox>
  );
};

const KioskItemLabel: FC<Pick<KioskListItem, 'label' | 'isPinned'>> = ({ label, isPinned }) => {
  const { t } = useTranslation('digital-forms-experience');
  return (
    <span css={itemLabelStyle}>
      {isPinned && <Icon name='pin' size={16} aria-label={t('Pinned')} />}
      <Text as='span' weight='bold'>
        {label}
      </Text>
    </span>
  );
};

const KioskFormListItem: FC<KioskListItem> = ({ shouldShowWeaveLogo, label, isPinned }) => {
  return (
    <>
      {shouldShowWeaveLogo && <WeaveLogoIcon />}
      <KioskItemLabel label={label} isPinned={isPinned} />
    </>
  );
};

const KioskPacketListItem: FC<KioskListItem> = ({ label, formsCount = 0, isPinned }) => {
  const { t } = useTranslation('digital-forms-experience');
  return (
    <>
      <KioskItemLabel label={label} isPinned={isPinned} />
      <Text color='primary' as='span' className='forms-count'>
        {formsCount} {t('Forms', { count: formsCount })}
      </Text>
    </>
  );
};

export const EmptyState: FC<{ type: 'forms' | 'packets' }> = ({ type }) => {
  const { t } = useTranslation('digital-forms-experience');
  return (
    <div css={emptyStateContainerStyle}>
      <Image
        src={`/images/no-${type}.png`}
        alt={t('Empty {{type}} section', { type })}
        css={responsiveImageStyle}
        animate={false}
        initial={false}
      />

      <Text weight='bold' textAlign='center'>
        {t('No {{type}} listed', { type: type === 'forms' ? 'Forms' : 'Packets' })}
      </Text>
      <Text color='light' textAlign='center'>
        {t('There are no results at the time')}
      </Text>
    </div>
  );
};
